import React, { Component } from 'react';
import PropTypes from 'prop-types';
import includes from 'lodash/includes';

import { SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK } from './constants';
import FacebookLoginComponent from './FacebookLoginComponent';
import ContinueWithFacebookButton from './FacebookButton';
import './facebookLoginButton.scss';

export default class FacebookLoginButton extends Component {
    static propTypes = {
        appId: PropTypes.string.isRequired,
        version: PropTypes.string,
        scope: PropTypes.string,
        fields: PropTypes.string,
        onSocialConnectVerified: PropTypes.func,
        onSocialConnectNotAuthorized: PropTypes.func,
        onSocialLogInUserAuthorized: PropTypes.func,
        onClick: PropTypes.func,
        isCheckoutAuthenticationFlow: PropTypes.bool,
        handleOnLoadComplete: PropTypes.func,
        handleOnNotConnected: PropTypes.func,
        handleOnComponentUpdated: PropTypes.func,
    };

    static defaultProps = {
        fields: 'name,email,picture,first_name,last_name',
    };

    state = {
        transitToLogin: false,
    };

    /**
       Called when the FacebookLoginComponent performs the autologin and the user is not logged
       on Facebook, in this scenario we should activate the transitToLogin state so following calls to
       handleResponse will start the login process
    */
    _onFacebookUserNotConnected = (status) => {
        const {
            onSocialConnectNotAuthorized,
            handleOnNotConnected,
        } = this.props;

        if (handleOnNotConnected) {
            handleOnNotConnected();
        } else {
            if (
                this.state.transitToLogin &&
                includes(['unknown', 'not_authorized'], status)
            ) {
                onSocialConnectNotAuthorized?.(
                    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
                    {
                        sourceProvider: SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
                        status,
                    },
                );
            }

            this.setState({
                status,
                transitToLogin: true,
            });
        }
    };

    /**
       Called after the FacebookLoginComponent get a success response from the Facebook login call, this method can be
       invoked either after the autologin or when the user clicks on Continue with Facebook button. It should only transit to
       login if we can ensure the user has clicked the button.
     */
    _onFacebookResponse = (response) => {
        const { onSocialConnectVerified } = this.props;
        const { transitToLogin } = this.state;
        const {
            accessToken,
            email,
            first_name: firstName,
            last_name: lastName,
            picture: { data: { url: userPicture } = {} } = {},
            userID,
        } = response;

        if (transitToLogin) {
            if (onSocialConnectVerified) {
                this.setState({
                    ...response,
                    status: null,
                });
                onSocialConnectVerified?.(
                    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
                    {
                        accessToken,
                        email,
                        sourceProvider: SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
                        firstName,
                        lastName,
                        userPicture,
                        userID,
                    },
                );
            }
        } else {
            this.setState({
                ...response,
                transitToLogin: true,
            });
        }
    };

    render() {
        const {
            appId,
            version,
            scope,
            fields,
            reRequestPermissions,
            handleOnLoadComplete,
            handleOnComponentUpdated,
            isCheckoutAuthenticationFlow,
            onSocialLogInUserAuthorized,
            onClick: onButtonClick,
            buttonFBText,
            skipScriptLoading,
        } = this.props;

        const {
            accessToken,
            picture,
            first_name: firstName,
            ...state
        } = this.state;
        const notLoggedIn = state.status === 'unknown' ? true : false;

        const renderButton = ({ onClick }) => {
            const _handleOnClick = () => {
                if (onButtonClick) {
                    const socialProvider = SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK;
                    onButtonClick(socialProvider, state);
                }
                onClick?.(SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK);
            };
            return (
                <ContinueWithFacebookButton
                    accessToken={accessToken}
                    picture={picture}
                    firstName={firstName}
                    handleOnClick={_handleOnClick}
                    buttonFBText={buttonFBText}
                />
            );
        };

        return (
            <FacebookLoginComponent
                appId={appId}
                onSocialConnectVerified={this.props.onSocialConnectVerified}
                handleOnResponse={this._onFacebookResponse}
                handleOnLoadComplete={handleOnLoadComplete}
                handleOnComponentUpdated={handleOnComponentUpdated}
                handleOnNotConnected={this._onFacebookUserNotConnected}
                onSocialLogInUserAuthorized={onSocialLogInUserAuthorized}
                notLoggedIn={notLoggedIn}
                isCheckoutAuthenticationFlow={isCheckoutAuthenticationFlow}
                renderProps={renderButton}
                version={version}
                autoLogin={true}
                scope={scope}
                fields={fields}
                cookie={true}
                reRequestPermissions={reRequestPermissions}
                skipScriptLoading={skipScriptLoading}
            />
        );
    }
}
